import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { toast } from "sonner";
import { MessageSquare, Mail, Phone, Calendar, FileText, User, Palette, Frame, Ruler, StickyNote } from "lucide-react";
import { API } from "../../apiConfig";

const STATUS_CONFIG = {
  new: { label: "New", variant: "destructive" },
  quoted: { label: "Quoted", variant: "default" },
  accepted: { label: "Accepted", variant: "outline" },
  completed: { label: "Completed", variant: "secondary" },
  declined: { label: "Declined", variant: "outline" },
};

const InquiriesTab = ({ token }) => {
  const [inquiries, setInquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchInquiries();
  }, [statusFilter]);

  const fetchInquiries = async () => {
    setLoading(true);
    try {
      const params = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const res = await axios.get(`${API}/inquiries${params}`, { headers });
      setInquiries(res.data.inquiries || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error("Failed to fetch inquiries:", err);
      toast.error("Failed to load inquiries");
    } finally {
      setLoading(false);
    }
  };

  const openDetail = (inquiry) => {
    setSelectedInquiry(inquiry);
    setAdminNotes(inquiry.admin_notes || "");
    setDetailOpen(true);
  };

  const updateStatus = async (newStatus) => {
    if (!selectedInquiry) return;
    setUpdatingStatus(true);
    try {
      await axios.put(
        `${API}/inquiries/${selectedInquiry.inquiry_id}/status`,
        { status: newStatus, admin_notes: adminNotes },
        { headers }
      );
      toast.success(`Inquiry marked as ${newStatus}`);
      setDetailOpen(false);
      fetchInquiries();
    } catch (err) {
      toast.error("Failed to update inquiry");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const newCount = inquiries.filter((i) => i.status === "new").length;

  return (
    <>
      <Card className="rounded-2xl" data-testid="inquiries-tab">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Inquiries & Quote Requests
            </CardTitle>
            <p className="text-sm text-muted-foreground mt-1">
              {total} total inquiries{newCount > 0 && ` — ${newCount} new`}
            </p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-36" data-testid="inquiries-status-filter">
              <SelectValue placeholder="Filter" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="quoted">Quoted</SelectItem>
              <SelectItem value="accepted">Accepted</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="declined">Declined</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-14 bg-muted rounded animate-pulse" />
              ))}
            </div>
          ) : inquiries.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-40" />
              <p className="font-medium">No inquiries yet</p>
              <p className="text-sm mt-1">
                Inquiries from the Quote Builder will appear here.
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Artwork</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Substrate / Size</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {inquiries.map((inq) => {
                  const cfg = STATUS_CONFIG[inq.status] || STATUS_CONFIG.new;
                  return (
                    <TableRow
                      key={inq.inquiry_id}
                      className="cursor-pointer hover:bg-muted/40"
                      onClick={() => openDetail(inq)}
                      data-testid={`inquiry-row-${inq.inquiry_id}`}
                    >
                      <TableCell>
                        <p className="font-medium line-clamp-1">
                          {inq.artwork_title}
                        </p>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm">{inq.name}</p>
                          <p className="text-xs text-muted-foreground">
                            {inq.email}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="capitalize text-sm">
                          {inq.substrate?.replace(/_/g, " ")}
                        </span>
                        {inq.size && (
                          <span className="text-xs text-muted-foreground ml-1">
                            · {inq.size}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant={cfg.variant}>{cfg.label}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {formatDate(inq.created_at)}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-full"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(inq);
                          }}
                          data-testid={`inquiry-view-${inq.inquiry_id}`}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail / Respond Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Inquiry Details</DialogTitle>
          </DialogHeader>

          {selectedInquiry && (
            <div className="space-y-5 py-2" data-testid="inquiry-detail-dialog">
              {/* Artwork */}
              <div className="p-4 bg-muted/40 rounded-xl space-y-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Palette className="h-4 w-4 text-primary" />
                  {selectedInquiry.artwork_title}
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <FileText className="h-3.5 w-3.5" />
                    <span className="capitalize">
                      {selectedInquiry.substrate?.replace(/_/g, " ")}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Ruler className="h-3.5 w-3.5" />
                    {selectedInquiry.size || "—"}
                  </div>
                  {selectedInquiry.framing && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Frame className="h-3.5 w-3.5" />
                      <span className="capitalize">
                        {selectedInquiry.framing}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Client info */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Client
                </Label>
                <div className="space-y-1 text-sm">
                  <div className="flex items-center gap-2">
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                    {selectedInquiry.name}
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                    <a
                      href={`mailto:${selectedInquiry.email}`}
                      className="text-primary underline"
                    >
                      {selectedInquiry.email}
                    </a>
                  </div>
                  {selectedInquiry.phone && (
                    <div className="flex items-center gap-2">
                      <Phone className="h-3.5 w-3.5 text-muted-foreground" />
                      {selectedInquiry.phone}
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Calendar className="h-3.5 w-3.5" />
                    {formatDate(selectedInquiry.created_at)}
                  </div>
                </div>
              </div>

              {/* Client notes */}
              {selectedInquiry.notes && (
                <div className="space-y-1">
                  <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                    Client Notes
                  </Label>
                  <p className="text-sm bg-muted/30 p-3 rounded-lg">
                    {selectedInquiry.notes}
                  </p>
                </div>
              )}

              {/* Admin notes */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Admin Notes
                </Label>
                <Textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="Internal notes — pricing, follow-up actions…"
                  rows={3}
                  data-testid="inquiry-admin-notes"
                />
              </div>

              {/* Status actions */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                  Update Status
                </Label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <Button
                      key={key}
                      size="sm"
                      variant={
                        selectedInquiry.status === key ? "default" : "outline"
                      }
                      className="rounded-full"
                      disabled={updatingStatus}
                      onClick={() => updateStatus(key)}
                      data-testid={`inquiry-status-${key}`}
                    >
                      {cfg.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default InquiriesTab;
